<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify';
import { VDateInput } from 'vuetify/labs/VDateInput'
import type { MDBSemi, MDBSemiRécolte } from '@/types/ferme/semi'
import { notifyError, roundTo, getSemiSuperficie, getSemiNbPlanches, toDate_wd_dd_MMMM } from '@/utils/helpers'
import { getSemiHistoriqueChangements } from '@/utils/historique'

const storeU = useUtilisateurStore()
const dialog = ref(false)
const modifications = ref(false)
const isEdit = ref(false)
const semi = ref({} as MDBSemi)
const semiSource = ref({} as MDBSemi)
const aUneDateEnSerre = ref(false)
const isRecalculatingDJ = ref(false)

const totalPlanches = computed(() => getSemiNbPlanches(semi.value as MDBSemi))
const superficie = computed(() => roundTo(getSemiSuperficie(semi.value), 2))
const estFarmit = computed(() => semi.value.farmitId != '' && semi.value.farmitId != undefined)
const culturesData = computed(() => storeU.cultures.map(c => c.nom))
const variétésData = computed(() => storeU.cultures.find(c => c.nom == semi.value.culture)?.variétés.map(v => v.nom).sort() || [])
const champsData = computed(() => storeU.champs.map(c => c.nom))
const lotsData = computed(() => storeU.champs.find(c => c.nom == semi.value.champ)?.lots.map(l => l.nom).sort() || [])
const djNécessaireCalculé = computed(() => roundTo(semi.value.degréJour?.djNécessaireCalculé ?? 0, 2))
const djRéelAccumulé = computed(() => roundTo(semi.value.degréJour?.djRéelAccumulé ?? 0, 2))
const dateDu = computed(() => toDate_wd_dd_MMMM(semi.value.dateDu))
const dateRécoltePrévue = computed(() => toDate_wd_dd_MMMM(semi.value.dateRécoltePrévue))
const dateRécolte = computed(() => toDate_wd_dd_MMMM(semi.value.dateRécolte))
const djDateRécoltePrévueInitiale = computed(() => toDate_wd_dd_MMMM(semi.value.degréJour?.dateRécoltePrévueInitiale ?? ''))
const djDateRécoltePrévue = computed(() => toDate_wd_dd_MMMM(semi.value.degréJour?.dateRécoltePrévue ?? ''))
const djRecalcul = computed(() => {
  if (semi.value.degréJour == null || semi.value.degréJour == undefined)
    return false

  const date = new Date(semi.value.degréJour.dateMiseÀJour)
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < now;
})
const dates = ref([] as Date[])
const isValidDateRécoltePrévueFarmit = computed(() => {
  const date = new Date(semi.value.dateDu)
  const dateR = new Date(semi.value.dateRécoltePrévue)
  return dateR.getFullYear() == date.getFullYear() && dateR.getTime() >= date.getTime()
})
const isValidDateRécolté = computed(() => {
  const date = new Date(semi.value.dateDu)
  const dateR = new Date(semi.value.dateRécolte)
  return dateR.getFullYear() == date.getFullYear() && dateR.getTime() >= date.getTime()
})

let resolve: (value: boolean) => void
watch(dialog, (v) => {
  if (!v) {
    resolve(modifications.value)
  }
})

function newSemi() : MDBSemi {
  return {
    siteId: storeU.siteSélectionné.id,
    année: storeU.annéeSélectionnée,
    numéro: '',
    dateDu: '',
    dateAu: '',
    dateRécolte: '',
    champ: '',
    lot: '',
    culture: '',
    variété: '',
    typePlantation: '',
    plancheDe: 0,
    plancheÀ: 0,
    longueur: 0,
    densité: 0,
    densitéPosition: 0,
    grainesParPied: 0,
    vitesse: 0,
    gear: '',
    commentaires: '',
    commentairesFarmit: '',
    dateRécoltePrévue: '',
    rendementPrévu: 0,
    récoltes: [] as MDBSemiRécolte[]
  } as MDBSemi
}

function openSemiNew() {
  semi.value = newSemi()
  isEdit.value = false
  dialog.value = true
  return new Promise<boolean>((resolveFn) => {
    resolve = resolveFn
  })
}

function openSemiEdit(semiEdit: MDBSemi) {
  isEdit.value = true
  semiSource.value = semiEdit
  semi.value = { ... semiEdit }

  aUneDateEnSerre.value = true
  if (new Date(semi.value.dateEnSerre).getFullYear() <= 2000) {
    aUneDateEnSerre.value = false
    semi.value.dateEnSerre = semi.value.dateDu
  }

  if (semiEdit.degréJour != null && semiEdit.degréJour != undefined)
    semi.value.degréJour = { ... semiEdit.degréJour }
  dialog.value = true
  return new Promise<boolean>((resolveFn) => {
    resolve = resolveFn
  })
}

function dateEnSerreChange() {
  aUneDateEnSerre.value = !aUneDateEnSerre.value
}

function aUneModification() {
  if (!aUneDateEnSerre.value) {
    semi.value.dateEnSerre = '2000-01-01T00:00:00Z'
  }

  if (isEdit.value && semi.value.farmitId != '') {
    if (semiSource.value.grainesParPied != semi.value.grainesParPied ||
        semiSource.value.vitesse != semi.value.vitesse ||
        semiSource.value.gear != semi.value.gear ||
        semiSource.value.dateEnSerre != semi.value.dateEnSerre ||
        semiSource.value.commentaires != semi.value.commentaires)
      return true

    if (semiSource.value.degréJour?.djNécessaireManuel != semi.value.degréJour?.djNécessaireManuel)
      return true

    return false
  }

  return true
}

async function confirm() {
  try
  {
    if (storeU.cultures.find(x => x.nom == semi.value.culture) == null) {
      Notify.error('Veuillez sélectionner une culture existente')
      return
    }

    const siteCS = storeU.getSiteLettresDappel(semi.value.siteId)
    if (isEdit.value) {

      if (!aUneModification()) {
        dialog.value = false
        return
      }

      await apiProphet.put(`sites/${siteCS}/semis/${semi.value.id}`, semi.value)
      Notify.success('Modification du semi avec succès');
    } else {
      semi.value.dateDu = dates.value[0].toISOString()
      semi.value.dateAu = dates.value[dates.value.length - 1].toISOString()
      await apiProphet.post(`sites/${siteCS}/semis`, semi.value)
      Notify.success('Création du semi avec succès');
      storeU.sélectionnerCulture(semi.value.culture, null)
    }

    modifications.value = true
    dialog.value = false
  }
  catch (e) {
    notifyError(e)
  }
}

async function recalculerDJMaintenant() {
  if (!isEdit.value || !semi.value.id)
    return

  try {
    isRecalculatingDJ.value = true
    const siteCS = storeU.getSiteLettresDappel(semi.value.siteId)
    const result = await apiProphet.put(`sites/${siteCS}/degrejours/miseajour/${semi.value.id}`, null, {
      params: {
        djNecessaireManuel: semi.value.degréJour?.djNécessaireManuel
      }
    })
    const semiApi = result.data as MDBSemi
    semi.value.degréJour = semiApi.degréJour
    modifications.value = true
    Notify.success('Degrés-jours recalculés avec succès')
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isRecalculatingDJ.value = false
  }
}

function cancel() {
  dialog.value = false
}

const dialogRécoltes = ref(false)
function openRécoltes() { dialogRécoltes.value = true }
function closeRécoltes() { dialogRécoltes.value = false }
const formattedRécoltes = computed(() => {
  return semi.value.récoltes.map(récolte => ({
    ...récolte,
    formattedDateRécoltes: new Date(récolte.dateRécolte).toLocaleDateString(),
    formattedDateRespectDAPTR: new Date(récolte.dateRespectDAPTR).toLocaleDateString()
  }));
});
const récoltesHeaders: DataTableHeaders = [
  { title: 'Produit', key: 'produit', width: '140px'},
  { title: 'Lot', key: 'lot', width: '120px' },
  { title: 'Date Récolte', key: 'formattedDateRécoltes', width: '120px' },
  { title: 'Date Respect DAPTR', key: 'formattedDateRespectDAPTR', width: '120px'},
  { title: 'Planches', key: 'planches', width: '80px'},
  { title: 'Quantité', key: 'quantité', width: '80px'},
  { title: 'Calibre', key: 'calibre', width: '80px'},
  { title: 'Priorité', key: 'priorité', width: '80px'}
];

const dialogHistorique = useTemplateRef('dialogHistorique')
function openHistorique() {
  dialogHistorique.value?.openHistAsync(async () => {
    const siteCS = storeU.getSiteLettresDappel(semi.value.siteId)
    const historique = [ semi.value ] as MDBSemi[]
    const resultHistorique = await apiProphet.get(`sites/${siteCS}/semis/${semi.value.id}/historique`)
    historique.push(...(resultHistorique.data as MDBSemi[]))
    return getSemiHistoriqueChangements(historique)
  })
}

const dialogBuckets = useTemplateRef('dialogBuckets')
async function openBuckets() {
  const modifs = await dialogBuckets.value?.openBucketsAsync(semi.value, null)
  if (modifs) {
    modifications.value = true
  }
}

onMounted(async () => {
  if (!storeU.siteSélectionné) {
    Notify.error('Veuillez sélectionner un site')
    cancel()
    return
  }
})

defineExpose({ openSemiNew, openSemiEdit })
</script>

<template>
  <v-dialog v-model="dialog" max-width="1000px" :fullscreen="$vuetify.display.smAndDown">
    <v-card style="z-index: -1">
      <v-card-title class="d-flex justify-space-between align-center">
        <span v-if="isEdit" class="text-h5 text-medium-emphasis ps-2">Modifier un semi</span>
        <span v-else class="text-h5 text-medium-emphasis ps-2">Nouveau semi</span>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="cancel"/>
      </v-card-title>

      <v-divider class="mb-4"/>

      <v-card-text>
        <v-container v-if="estFarmit">
          <v-row>
            <v-col cols="12" md="3" sm="6">
              <v-text-field v-model="semi.farmitId" label="ID Farmit" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="semi.numéro" label="Numéro" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="semi.culture" label="Culture" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="semi.variété" label="Variété" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="semi.champ" label="Champ" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="semi.lot" label="Lot" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="semi.typePlantation" label="Type de plantation" readonly density="compact" variant="outlined"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
              <v-text-field v-model="semi.plancheDe" label="Planche de" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="semi.plancheÀ" label="Planche à" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="totalPlanches" label="Total planches" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="semi.longueur" label="Longueur" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="superficie" label="Superficie" type="number" readonly density="compact" variant="outlined"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
              <v-text-field v-model="semi.densité" label="Densité" type="number" density="compact" variant="outlined"/>
              <v-text-field v-model="semi.densitéPosition" label="Densité position" type="number" density="compact" variant="outlined"/>
              <v-text-field v-model="semi.grainesParPied" label="Graines par pied" type="number" required/>
              <v-text-field v-model="semi.vitesse" label="Vitesse" type="number" required/>
              <v-text-field v-model="semi.gear" label="Gear" required/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
              <v-text-field v-model="dateDu" label="Date plantation" readonly density="compact" variant="outlined"/>
              <v-text-field v-if="isValidDateRécoltePrévueFarmit" v-model="dateRécoltePrévue" label="Date réc. prévue Farmit" readonly density="compact" variant="outlined"/>
              <v-text-field v-else label="Date réc. prévue Farmit" readonly density="compact" variant="outlined"/>
              <v-text-field v-if="isValidDateRécolté" v-model="dateRécolte" label="Date récolté" readonly density="compact" variant="outlined"/>
              <v-text-field v-else label="Date récolté" readonly density="compact" variant="outlined"/>
              <v-date-input
                v-if="aUneDateEnSerre" v-model="semi.dateEnSerre" label="Date en serre"
                display-format="normalDateWithWeekday" prepend-icon=""/>
              <v-btn
                color="secondary"
                variant="flat"
                :text="aUneDateEnSerre ? 'Supprimer date en serre' : 'Ajouter date en serre'"
                @click="dateEnSerreChange"/>
            </v-col>
          </v-row>
        </v-container>
        <v-container v-else>
          <v-row>
            <v-col cols="12" md="3" sm="6">
              <v-text-field v-model="semi.numéro" label="Numéro" required/>
              <v-select v-model="semi.culture" :items="culturesData" label="Culture" required hide-no-data @update:model-value="() => semi.variété = ''" />
              <v-select v-model="semi.variété" :items="variétésData" label="Variété" required/>
              <v-combobox v-model="semi.champ" :items="champsData" label="Champ" required @update:model-value="() => semi.lot = ''"/>
              <v-combobox v-model="semi.lot" :items="lotsData" label="Lot" required/>
              <v-text-field v-model="semi.typePlantation" label="Type de plantation" required/>
              <v-date-input v-model="dates" label="Date plantation" multiple="range" required/>
              <v-date-input v-model="semi.dateRécolte" label="Date récolté"/>
              <v-date-input v-model="semi.dateEnSerre" label="Date en serre"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
              <v-text-field v-model="semi.plancheDe" label="Planche de" type="number" required/>
              <v-text-field v-model="semi.plancheÀ" label="Planche à" type="number" required/>
              <v-text-field v-model="totalPlanches" label="Total planches" type="number" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="semi.longueur" label="Longueur" type="number" required/>
              <v-text-field v-model="superficie" label="Superficie" type="number" readonly density="compact" variant="outlined"/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
              <v-text-field v-model="semi.densité" label="Densité" type="number" required/>
              <v-text-field v-model="semi.densitéPosition" label="Densité position" type="number" required/>
              <v-text-field v-model="semi.grainesParPied" label="Graines par pied" type="number" required/>
              <v-text-field v-model="semi.vitesse" label="Vitesse" type="number" required/>
              <v-text-field v-model="semi.gear" label="Gear" required/>
            </v-col>
            <v-col cols="12" md="3" sm="6">
              <v-date-input v-model="semi.dateRécoltePrévue" label="Date récolte prévue" required/>
              <v-text-field v-model="semi.rendementPrévu" label="Rendement prévu" type="number" required/>
              <!--<v-text-field v-model="nbBoîtes" label="Nombre de boîtes" type="number" readonly density="compact" variant="outlined"/>-->
            </v-col>
          </v-row>
        </v-container>

        <v-container v-if="semi.degréJour != null">
          <v-card>
            <v-card-title>Degré-jour</v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12" md="3" sm="6">
                    <v-text-field v-model="djRéelAccumulé" label="DJ réels accumulés" type="number" readonly density="compact" variant="outlined"/>
                    <v-checkbox v-model="semi.degréJour.complété" label="Récolté" readonly density="compact" variant="outlined"/>
                    <span v-if="djRecalcul" class="text-danger">Recalcul des DJ à venir</span>
                  </v-col>
                  <v-col cols="12" md="3" sm="6">
                    <v-text-field v-model="djNécessaireCalculé" label="DJ nécessaires calculé" type="number" readonly density="compact" variant="outlined"/>
                    <v-text-field v-model="semi.degréJour.djNécessaireManuel" label="DJ nécessaires manuel" type="number" required/>
                    <v-btn
                      color="secondary" variant="tonal" :loading="isRecalculatingDJ"
                      :disabled="isRecalculatingDJ || !isEdit" @click="recalculerDJMaintenant">
                      Recalculer maintenant
                    </v-btn>
                  </v-col>
                  <v-col cols="12" md="3" sm="6">
                    <v-text-field v-model="djDateRécoltePrévueInitiale" label="Date de récolte prévue initiale" readonly density="compact" variant="outlined"/>
                    <v-text-field v-model="djDateRécoltePrévue" label="Date de récolte prévue à jour" readonly density="compact" variant="outlined"/>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-container>

        <v-container>
          <v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea v-model="semi.commentaires" rows="2" label="Commentaires" required/>
                <v-textarea v-if="estFarmit" v-model="semi.commentairesFarmit" rows="2" label="Commentaires Farmit" readonly variant="outlined"/>
              </v-col>
            </v-row>
          </v-row>
          <v-row v-if="isEdit">
            <v-btn color="secondary" variant="flat" class="mr-4 mt-2" @click="openRécoltes">Voir récoltes</v-btn>
            <v-btn color="secondary" variant="flat" class="mr-4 mt-2" @click="openHistorique">Voir historique</v-btn>
            <v-btn color="secondary" variant="flat" class="mr-4 mt-2" @click="openBuckets">Voir buckets</v-btn>
          </v-row>
        </v-container>
      </v-card-text>

      <v-divider class="mt-2"/>

      <v-card-actions class="d-flex justify-end mr-2 my-2">
        <v-btn @click="cancel">Annuler</v-btn>
        <v-btn v-if="isEdit" color="primary" variant="flat" @click="confirm">Modifier le semi</v-btn>
        <v-btn v-else color="primary" variant="flat" @click="confirm">Créer un nouveau semi (non-farmit)</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <DialogHistorique ref="dialogHistorique"/>
  <DialogBuckets ref="dialogBuckets"/>

  <v-dialog v-model="dialogRécoltes" max-width="80%" :fullscreen="$vuetify.display.smAndDown">
    <v-card style="z-index: -1">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5">Récoltes</span>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="closeRécoltes"/>
      </v-card-title>
      <v-divider class="mb-4"/>
      <v-card-text>
        <v-data-table
          :headers="récoltesHeaders" :items="formattedRécoltes"
          :hide-default-header="$vuetify.display.smAndDown">
          <template v-if="$vuetify.display.smAndDown" #item="props">
            <tr>
              <td class="pt-2 pb-2">
                Produit: {{ props.item.produit }}<br/>
                Lot: {{ props.item.lot }}<br/>
                Date récolte: {{ props.item.formattedDateRécoltes }}<br/>
                Date respect DAPTR: {{ props.item.formattedDateRespectDAPTR }}<br/>
                Planches: {{ props.item.planches }}<br/>
                Quantité: {{ props.item.quantité }}<br/>
                Calibre: {{ props.item.calibre }}<br/>
                Priorité: {{ props.item.priorité }}<br/>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
      <v-divider/>
      <v-card-actions>
        <v-btn color="primary" variant="flat" @click="closeRécoltes">OK</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
